import { Link } from 'react-router-dom'
import { X, Bell, Loader2, Lock } from 'lucide-react'
import { useEmailPrefs } from '@/hooks/useEmailPrefs'
import { formatHourLabel, detectTimezone } from '@/lib/dailyEmail'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { useSoundOnToggle } from '@/hooks/useSoundEffects'
import { ProBadge } from '@/components/ProBadge'

const HOURS = Array.from({ length: 24 }, (_, h) => h)

// Daily reminder email settings, surfaced from the Dashboard header via the
// notification icon — the one place the preference is set: the opt-in toggle
// plus a local send-time picker persisted to user_email_prefs. The email itself is sent server-side by the
// daily-plan-email edge function (see docs/daily-plan-email.md).
//
// The email is today's study plan, and the study plan is Pro, so the email is
// too: a free account sees the lock and an Upgrade link instead of the toggle,
// and the sender skips any account that isn't Pro (an opt-in kept from a lapsed
// subscription stays stored, and resumes if they subscribe again).

interface Props {
  open: boolean
  onClose: () => void
  email: string
  isPro: boolean
  /** The subscription is still resolving — don't flash the lock at a Pro reader. */
  subscriptionLoading: boolean
}

export function DashboardRemindersModal({ open, onClose, email, isPro, subscriptionLoading }: Props) {
  // Paper: the panel sliding in.
  useSoundOnToggle(open, 'open', 'close')
  const { prefs, loading, saving, error, save } = useEmailPrefs()

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-[70] flex items-start justify-center bg-background/80 backdrop-blur-sm p-4 overflow-y-auto paper-scrim"
      role="dialog"
      aria-modal="true"
      aria-label="Daily reminder email settings"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="w-full max-w-lg bg-card rounded-xl shadow-2xl flex flex-col my-12">
        {/* Header */}
        <div className="flex items-center gap-2 px-4 h-12 shrink-0">
          <Bell className="h-4 w-4 text-primary shrink-0" />
          <span className="flex-1 flex items-center gap-1.5 font-semibold text-sm">
            Daily reminder email
            {!isPro && !subscriptionLoading && <ProBadge />}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground p-2 transition-colors rounded-md hover:bg-muted/50"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-muted-foreground">
            A short daily email with the concepts your study plan has scheduled for the day,
            sent to <span className="font-medium text-foreground">{email}</span>.
            Days with nothing scheduled are skipped.
          </p>

          {loading || subscriptionLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : !isPro ? (
            <div className="flex items-start gap-2 rounded-lg bg-primary/5 px-3 py-2 text-sm text-muted-foreground">
              <Lock className="h-4 w-4 shrink-0 mt-0.5 text-primary/60" />
              <span>The daily email sends your study plan, which comes with Pro.</span>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-4">
                <Label htmlFor="dashboard-reminder-toggle" className="cursor-pointer">
                  Email me my study plan every day
                </Label>
                <button
                  id="dashboard-reminder-toggle"
                  type="button"
                  role="switch"
                  aria-checked={prefs.dailyPlanEmail}
                  disabled={saving}
                  onClick={() => void save({ dailyPlanEmail: !prefs.dailyPlanEmail })}
                  className={cn(
                    'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors',
                    prefs.dailyPlanEmail ? 'bg-primary' : 'bg-muted-foreground/30',
                    saving && 'opacity-60',
                  )}
                >
                  <span
                    className={cn(
                      'inline-block h-5 w-5 transform rounded-full bg-background shadow transition-transform',
                      prefs.dailyPlanEmail ? 'translate-x-[22px]' : 'translate-x-0.5',
                    )}
                  />
                </button>
              </div>

              {prefs.dailyPlanEmail && (
                <div className="flex items-center justify-between gap-4">
                  <Label htmlFor="dashboard-reminder-hour">Send at</Label>
                  <select
                    id="dashboard-reminder-hour"
                    value={prefs.sendHourLocal}
                    disabled={saving}
                    onChange={e => void save({ sendHourLocal: Number(e.target.value) })}
                    className="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    {HOURS.map(h => (
                      <option key={h} value={h}>{formatHourLabel(h)}</option>
                    ))}
                  </select>
                </div>
              )}

              {prefs.dailyPlanEmail && (
                <p className="text-xs text-muted-foreground">
                  Times are in your current timezone ({detectTimezone()}). Changing this setting
                  from another device updates the timezone to match it.
                </p>
              )}

              {error && <p className="text-xs text-destructive">{error}</p>}
            </>
          )}
        </div>

        <div className="px-5 pb-5 flex justify-end gap-2">
          {!isPro && !subscriptionLoading && (
            <Link
              to="/upgrade"
              onClick={onClose}
              className="rounded-md px-3 py-2 text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Upgrade
            </Link>
          )}
          <button
            type="button"
            onClick={onClose}
            className={cn(
              'rounded-md px-3 py-2 text-sm font-medium transition-colors',
              isPro || subscriptionLoading
                ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                : 'bg-muted text-foreground hover:bg-accent',
            )}
          >
            {isPro || subscriptionLoading ? 'Done' : 'Not now'}
          </button>
        </div>
      </div>
    </div>
  )
}
